/**
 * Accessibility Fixes for ADA Compliance - Streamlined Version
 * Focuses on issues that cannot be solved with CSS alone
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    debounceDelay: 100,
    maxAltLength: 125,
    observerThrottle: 50
  };

  // Debounce function to limit function calls
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Fix duplicate alt text for different images
  function fixDuplicateAltText() {
    const images = document.querySelectorAll('img[alt]');
    const altTextMap = new Map();

    images.forEach(function(img, index) {
      const altText = img.getAttribute('alt').trim();
      const src = img.getAttribute('src') || img.getAttribute('data-src');

      if (altText && src) {
        if (altTextMap.has(altText)) {
          // Found duplicate alt text for different images
          const existingImages = altTextMap.get(altText);
          const isDifferentImage = !existingImages.some(function(existingImg) {
            const existingSrc = existingImg.getAttribute('src') || existingImg.getAttribute('data-src');
            return existingSrc === src;
          });

          if (isDifferentImage) {
            // Make alt text unique by adding distinguishing information
            if (img.closest('.ProductItem__Image--alternate')) {
              img.setAttribute('alt', altText + ' - alternate view');
            } else if (img.closest('.ProductItem')) {
              img.setAttribute('alt', altText + ' - product image ' + (existingImages.length + 1));
            } else {
              img.setAttribute('alt', altText + ' - image ' + (existingImages.length + 1));
            }
          }

          existingImages.push(img);
        } else {
          altTextMap.set(altText, [img]);
        }
      }
    });
  }

  // Fix long alt attributes (over 125 characters)
  function fixLongAltAttributes() {
    const images = document.querySelectorAll('img[alt]');

    images.forEach(function(img) {
      const altText = img.getAttribute('alt');
      if (altText && altText.length > CONFIG.maxAltLength) {
        // Create a shorter, more concise alt text
        let shortAlt = altText;

        // If it contains product information, extract the key parts
        if (altText.includes('woman') && altText.includes('wearing')) {
          // Extract product name if possible
          const productMatch = altText.match(/wearing\s+(?:a\s+)?([^.]+?)(?:\s+stacked|\s+on)/i);
          if (productMatch) {
            shortAlt = 'Woman wearing ' + productMatch[1].trim();
          } else {
            shortAlt = 'Woman wearing jewelry';
          }
        } else if (altText.includes('DISCLAIMER') || altText.includes('Discount')) {
          // For promotional images
          shortAlt = 'Sale promotion image';
        } else if (altText === 'Image') {
          // For generic "Image" alt text
          const productTitle = img.closest('.ProductItem')?.querySelector('.ProductItem__Title a')?.textContent;
          if (productTitle) {
            shortAlt = productTitle.trim();
          } else {
            shortAlt = 'Product image';
          }
        } else {
          // Generic fallback - take first 100 characters and add ellipsis
          shortAlt = altText.substring(0, 100).trim() + '...';
        }

        // Set the shorter alt text
        img.setAttribute('alt', shortAlt);

        // Store original alt text in data attribute if needed for accessibility tools
        img.setAttribute('data-original-alt', altText);
      }
    });
  }

  // Fix inconsistent link text for same targets
  function fixInconsistentLinkText() {
    const links = document.querySelectorAll('a[href]');
    const linkMap = new Map();

    links.forEach(function(link) {
      const href = link.getAttribute('href');
      const text = link.textContent.trim();
      const ariaLabel = link.getAttribute('aria-label');

      if (href && (text || ariaLabel)) {
        if (linkMap.has(href)) {
          const existingLink = linkMap.get(href);
          // Standardize to the most descriptive aria-label or text
          let standardText = existingLink.ariaLabel || existingLink.text;
          let currentText = ariaLabel || text;

          // Choose the longer, more descriptive text
          if (currentText.length > standardText.length) {
            standardText = currentText;
          }

          // Apply standardized aria-label to both links
          if (standardText !== text) {
            link.setAttribute('aria-label', standardText);
          }
          if (standardText !== existingLink.text && standardText !== existingLink.ariaLabel) {
            existingLink.element.setAttribute('aria-label', standardText);
          }
        } else {
          linkMap.set(href, { text: text, ariaLabel: ariaLabel, element: link });
        }
      }
    });
  }

  // Fix accessible name mismatch with visible labels
  function fixAccessibleNameMismatch() {
    const links = document.querySelectorAll('a[aria-label]');

    links.forEach(function(link) {
      const ariaLabel = link.getAttribute('aria-label');
      const visibleText = link.textContent.trim();

      // Remove problematic image HTML from aria-labels
      if (ariaLabel && ariaLabel.includes('<img')) {
        // Extract meaningful text from complex aria-labels
        if (ariaLabel.includes('Latest Arrivals')) {
          link.setAttribute('aria-label', 'Shop Latest Arrivals');
        } else if (ariaLabel.includes('Heartstrings')) {
          link.setAttribute('aria-label', 'Shop Heartstrings Collection');
        } else if (ariaLabel.includes('Paracord')) {
          link.setAttribute('aria-label', 'Shop Paracord Collection');
        } else if (ariaLabel.includes('Tennis')) {
          link.setAttribute('aria-label', 'Shop Tennis Collection');
        } else if (ariaLabel.includes('Hinged Huggie')) {
          link.setAttribute('aria-label', 'Shop Hinged Huggie Collection');
        } else if (visibleText) {
          link.setAttribute('aria-label', visibleText);
        } else {
          link.removeAttribute('aria-label');
        }
      }

      // Fix shipping message aria-label mismatch
      if (ariaLabel === 'Free shipping on all U.S. orders over $300! See more for details.' &&
          visibleText === 'Free shipping on all U.S. orders over $300! See more for details.') {
        link.removeAttribute('aria-label'); // Text matches, aria-label is redundant
      }
    });
  }

  // Fix empty alt attributes or missing alt attributes
  function fixEmptyAltAttributes() {
    const images = document.querySelectorAll('img');

    images.forEach(function(img) {
      const altText = img.getAttribute('alt');
      const src = img.getAttribute('src') || img.getAttribute('data-src');

      // If image has no alt attribute at all, add an appropriate one
      if (!img.hasAttribute('alt')) {
        // Check if it's likely a decorative image
        if (img.closest('.Icon, .PlaceholderBackground') ||
            img.classList.contains('icon') ||
            img.classList.contains('decorative') ||
            (src && (src.includes('placeholder') || src.includes('no-image')))) {
          img.setAttribute('alt', '');
          img.setAttribute('aria-hidden', 'true');
        } else {
          // Try to get context from surrounding elements
          const productTitle = img.closest('.ProductItem')?.querySelector('.ProductItem__Title a')?.textContent;
          const collectionTitle = img.closest('.CollectionItem')?.querySelector('.SectionHeader__Heading')?.textContent;

          if (productTitle) {
            img.setAttribute('alt', productTitle.trim());
          } else if (collectionTitle) {
            img.setAttribute('alt', collectionTitle.trim());
          } else {
            img.setAttribute('alt', 'Product image');
          }
        }
      }
      // If alt attribute exists but is empty, ensure it's marked as decorative
      else if (altText === '') {
        img.setAttribute('aria-hidden', 'true');
      }
      // Fix generic "Image" alt text
      else if (altText === 'Image') {
        const productTitle = img.closest('.ProductItem')?.querySelector('.ProductItem__Title a')?.textContent;
        if (productTitle) {
          img.setAttribute('alt', productTitle.trim());
        } else {
          img.setAttribute('alt', 'Product image');
        }
      }
    });
  }

  // Fix duplicate IDs
  function fixDuplicateIds() {
    const elementsWithIds = document.querySelectorAll('[id]');
    const idMap = new Map();

    elementsWithIds.forEach(function(element) {
      const id = element.id;
      if (idMap.has(id)) {
        // Generate unique ID
        const counter = idMap.get(id) + 1;
        element.id = id + '_' + counter;
        idMap.set(id, counter);
      } else {
        idMap.set(id, 1);
      }
    });
  }

  // Add new tab warnings to external links
  function addNewTabWarnings() {
    const externalLinks = document.querySelectorAll('a[target="_blank"]');

    externalLinks.forEach(function(link) {
      const ariaLabel = link.getAttribute('aria-label') || '';
      const linkText = link.textContent.trim();

      // Only add warning if not already present
      if (!ariaLabel.includes('new tab') && !ariaLabel.includes('new window') &&
          !linkText.includes('new tab') && !linkText.includes('new window')) {

        if (ariaLabel) {
          link.setAttribute('aria-label', ariaLabel + ' - opens in a new tab');
        } else if (linkText) {
          link.setAttribute('aria-label', linkText + ' - opens in a new tab');
        }
      }
    });
  }

  // Initialize all accessibility fixes
  function init() {
    const runFixes = function() {
      try {
        fixLongAltAttributes();
        fixDuplicateAltText();
        fixInconsistentLinkText();
        fixAccessibleNameMismatch();
        fixEmptyAltAttributes();
        fixDuplicateIds();
        addNewTabWarnings();

        console.log('Accessibility fixes applied successfully');
      } catch (error) {
        console.warn('Some accessibility fixes failed to initialize:', error);
      }
    };

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runFixes);
    } else {
      runFixes();
    }

    // Re-run fixes when content changes (for dynamic content)
    const observer = new MutationObserver(debounce(runFixes, CONFIG.debounceDelay));
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize the accessibility fixes
  init();

})();
